<template>
  <v-container class="specific-percentages-container">
    <ValidationObserver ref="observer">
      <div
        v-for="(specificPercentage, index) in specificPercentages"
        :key="index"
      >
        <v-row no-gutters>
          <v-col cols="5" class="list-item">
            <v-btn :ripple="false" block color="white" class="list-item-btn">
              <div class="list-item-text">
                <span class="list-item-name">{{
                  specificPercentage.beneficiaryDisplayName
                }}</span>
              </div>
            </v-btn>
          </v-col>

          <v-col
            cols="4"
            sm="4"
            offset-sm="1"
            class="pl-4 pl-sm-0"
            v-if="!distributeEqually"
          >
            <ValidationProvider
              :id="uniqueKey(specificPercentage)"
              v-slot="{ errors }"
              :vid="uniqueKey(specificPercentage)"
              name="percentage"
              rules="required|max_value:100|integer|min_value:1"
            >
              <TextField
                v-model="specificPercentage.percentage"
                :suffix="'%'"
                :type="'number'"
                :errors="errors || []"
                :name="'percentage'"
                :remainder-of-estate-margin="true"
              />
            </ValidationProvider>
          </v-col>

          <v-col sm="1" cols="2" offset-sm="1">
            <v-btn
              rounded
              depressed
              outlined
              color="grey darken-3"
              :small="$vuetify.breakpoint.xsOnly"
              class="delete-btn"
              :class="{ 'small-delete-btn': $vuetify.breakpoint.xsOnly }"
              @click="removeBeneficiary(index)"
            >
              Delete
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <v-row class="total-percentage-container" no-gutters>
        <v-col sm="7" cols="6">
          <h2 class="total-percentage-title">Total Percentage</h2>
        </v-col>

        <v-col sm="5" cols="6">
          <div class="total-percentage">{{ totalPercentage }}%</div>
          <ValidationProvider
            id="total_percentage"
            v-slot="{ errors }"
            name="total_percentage"
            :rules="{ is: 100 }"
          >
            <input v-model="totalPercentage" type="hidden" disabled />
            <span class="error-text">{{
              errors.length
                ? specificPercentages.length
                  ? 'The total percentage should be 100%.'
                  : 'Add at least one beneficiary.'
                : ''
            }}</span>
          </ValidationProvider>
        </v-col>
      </v-row>

      <div class="percentage-buttons-container">
        <div class="add-beneficiary">
          <AlternativeButton
            :title="'Add person'"
            @gradientButtonClicked="showPersonDialog = true"
          />
        </div>

        <div class="add-beneficiary">
          <AlternativeButton
            :title="'Add charity'"
            @gradientButtonClicked="showCharityDialog = true"
          />
        </div>

        <div v-if="showAddTrust" class="add-beneficiary">
          <AlternativeButton
            :title="'Add Trust'"
            @gradientButtonClicked="showTrustDialog = true"
          />
        </div>
      </div>
      <div>
        <Checkbox
          v-model="distributeEqually"
          :label="`Distribute equally among all parties`"
          color="primary"
          @change="distributeEquallyChanged"
        />
      </div>

      <v-row no-gutters>
        <v-col cols="12">
          <GradientButton
            :title="'Next'"
            :small="true"
            :callout="'right'"
            :loading="buttonLoading"
            @gradientButtonClicked="submit"
          />
        </v-col>
      </v-row>
    </ValidationObserver>

    <PersonDialog
      v-model="showPersonDialog"
      :people-list-for-dialog="peopleListForDialog"
      relation="HEIR"
      :create-new-relationship="false"
      :person-to-update="{}"
      @existingPersonSelected="personSelected"
      @personCreated="personSelected"
    />
    <CharityDialog
      v-model="showCharityDialog"
      :existing-charities="charitiesListForDialog"
      @existingCharitySelected="charitySelected"
      @charityCreated="charitySelected"
    />
    <TrustDialog
      v-model="showTrustDialog"
      :existing-trusts="trustsListForDialog"
      @existingTrustSelected="trustSelected"
      @trustCreated="trustSelected"
    />
  </v-container>
</template>

<script>
  import PersonDialog from '../people/PersonDialog.vue';
  import CharityDialog from '../people/CharityDialog.vue';
  import TrustDialog from '../people/TrustDialog.vue';

  export default {
    name: 'AssignSpecificPercentages',
    components: {
      PersonDialog,
      CharityDialog,
      TrustDialog,
    },
    props: {
      testator: {
        type: Object,
        required: true,
      },
      existingSpecificPercentages: {
        type: Array,
        required: true,
      },
      buttonLoading: {
        type: Boolean,
        default: false,
      },
      parentBeneficiaryId: {
        type: String,
        required: false,
      },
      // [{ beneficiaryType, beneficiaryId }] that may not be selected here, e.g. people
      // already named elsewhere in the remainder of estate.
      excludedBeneficiaries: {
        type: Array,
        default: () => [],
      },
      distribution: {
        required: false,
        type: String,
      },
    },
    data() {
      return {
        showPersonDialog: false,
        showCharityDialog: false,
        showTrustDialog: false,
        // using native JSON functions removes reactivity
        // so we can clone an object without mutating the original source
        specificPercentages: JSON.parse(
          JSON.stringify(this.existingSpecificPercentages)
        ),
        deletedRecordIds: [],
        distributeEqually: null,
      };
    },
    mounted() {
      this.checkIfEquallyDistributed();
    },
    watch: {
      existingSpecificPercentages(newValue, oldValue) {
        this.showPersonDialog = false;
        this.showCharityDialog = false;
        this.showTrustDialog = false;

        let leftoverPercentages = [];
        this.existingSpecificPercentages.forEach(percentage => {
          if (!this.deletedRecordIds.includes(percentage.id)) {
            leftoverPercentages.push(percentage);
          }
        });

        this.specificPercentages = JSON.parse(
          JSON.stringify(leftoverPercentages)
        );
        this.$refs.observer.reset();

        this.checkIfEquallyDistributed();
      },
      specificPercentages: {
        handler() {
          this.$forceUpdate();

          if (this.distributeEqually) {
            this.checkIfEquallyDistributed();
          }
        },
        deep: true,
      },
    },
    computed: {
      totalPercentage() {
        const total = this.specificPercentages.reduce((total, p) => {
          const percentage = p.percentage;
          return percentage && percentage !== ''
            ? total + parseInt(percentage)
            : total;
        }, 0);

        // Uncheck distribute equally if total exceeds 100%
        if (total > 100 && this.distributeEqually) {
          this.distributeEqually = false;
        }

        return total;
      },
      peopleListForDialog() {
        const alreadyAllocatedPeopleIds = this.specificPercentages
          .filter(sp => sp.beneficiaryType == 'Person')
          .map(sp => sp.beneficiaryId);

        if (this.distribution) {
          const spouse = this.testator.people.find(
            person => person.kinship === this.distribution
          )?.id;
          if (spouse) {
            alreadyAllocatedPeopleIds.push(spouse);
          }
        }
        let people = this.testator.people.map(p => {
          return alreadyAllocatedPeopleIds.includes(p.id)
            ? { ...p, disabledMessage: 'This person has already been selected' }
            : p;
        });

        return this.applyExclusion(
          people.map(p => {
            return [this.parentBeneficiaryId].includes(p.id)
              ? { ...p, disabledMessage: 'Invalid selection' }
              : p;
          }),
          'Person'
        );
      },
      charitiesListForDialog() {
        const alreadyAllocatedCharityIds = this.specificPercentages
          .filter(sp => sp.beneficiaryType == 'Charity')
          .map(sp => sp.beneficiaryId);

        return this.applyExclusion(
          this.testator.charities.map(c => {
            return alreadyAllocatedCharityIds.includes(c.id)
              ? {
                  ...c,
                  disabledMessage: 'This charity has already been selected',
                }
              : c;
          }),
          'Charity'
        );
      },
      trustsListForDialog() {
        const alreadyAllocatedTrustIds = this.specificPercentages
          .filter(sp => sp.beneficiaryType == 'Trust')
          .map(sp => sp.beneficiaryId);

        return this.applyExclusion(
          this.testator.trusts.map(t => {
            return alreadyAllocatedTrustIds.includes(t.id)
              ? {
                  ...t,
                  disabledMessage: 'This trust has already been selected',
                }
              : t;
          }),
          'Trust'
        );
      },
      showAddTrust() {
        return this.testator.willCountry != 'GB';
      },
    },
    methods: {
      isExcluded(beneficiaryType, beneficiaryId) {
        return this.excludedBeneficiaries.some(
          excluded =>
            excluded.beneficiaryType === beneficiaryType &&
            String(excluded.beneficiaryId) === String(beneficiaryId)
        );
      },
      // Applied last, so the exclusion reason wins over the more generic
      // "already been selected" / "Invalid selection" messages.
      applyExclusion(items, beneficiaryType) {
        return items.map(item =>
          this.isExcluded(beneficiaryType, item.id)
            ? {
                ...item,
                disabledMessage: 'Already named in your remainder of estate',
              }
            : item
        );
      },
      personSelected(person) {
        this.beneficiarySelected(person, 'Person');
        if (this.distributeEqually) {
          this.distributeEquallyChanged();
        }
      },
      charitySelected(charity) {
        this.beneficiarySelected(charity, 'Charity');
        if (this.distributeEqually) {
          this.distributeEquallyChanged();
        }
      },
      trustSelected(charity) {
        this.beneficiarySelected(charity, 'Trust');
        if (this.distributeEqually) {
          this.distributeEquallyChanged();
        }
      },
      beneficiarySelected(beneficiary, beneficiaryType) {
        var newSpecificPercentage = {};
        newSpecificPercentage.beneficiaryId = beneficiary.id;
        newSpecificPercentage.beneficiaryType = beneficiaryType;
        newSpecificPercentage.beneficiaryDisplayName = beneficiary.displayName;
        this.specificPercentages.push(newSpecificPercentage);
      },
      removeBeneficiary(index) {
        if (this.specificPercentages[index].id) {
          this.deletedRecordIds.push(this.specificPercentages[index].id);
        }

        this.specificPercentages.splice(index, 1);
        if (this.distributeEqually) {
          this.distributeEquallyChanged();
        }
      },
      uniqueKey(specificPercentage) {
        return (
          specificPercentage.beneficiaryType + specificPercentage.beneficiaryId
        );
      },
      submit() {
        if (this.buttonLoading) return;
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.$emit('submit', {
              specificPercentages: this.specificPercentages,
              deletedRecordIds: this.deletedRecordIds,
              // distributeEqually starts as null, so coerce for callers that pass it
              // straight to a non-null GraphQL Boolean argument.
              distributeEqually: Boolean(this.distributeEqually),
            });
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      checkIfEquallyDistributed() {
        if (this.specificPercentages.length === 0) {
          this.distributeEqually = false;
          return;
        }

        const validPercentages = this.specificPercentages.filter(
          p =>
            p.percentage &&
            p.percentage !== '' &&
            !isNaN(parseInt(p.percentage))
        );

        if (validPercentages.length !== this.specificPercentages.length) {
          this.distributeEqually = false;
          return;
        }

        const equalPercentage = Math.floor(
          100 / this.specificPercentages.length
        );
        const remainder = 100 % this.specificPercentages.length;

        const isEqual = this.specificPercentages.every((beneficiary, index) => {
          const expectedPercentage =
            equalPercentage + (index < remainder ? 1 : 0);
          return parseInt(beneficiary.percentage) === expectedPercentage;
        });

        this.distributeEqually = isEqual;
      },
      distributeEquallyChanged() {
        if (this.distributeEqually) {
          const equalPercentage =
            this.specificPercentages.length > 0
              ? Math.floor(100 / this.specificPercentages.length)
              : 0;
          const remainder =
            this.specificPercentages.length > 0
              ? 100 % this.specificPercentages.length
              : 0;

          // Use Vue.set or $set to ensure reactivity
          this.specificPercentages.forEach((beneficiary, index) => {
            this.$set(
              beneficiary,
              'percentage',
              equalPercentage + (index < remainder ? 1 : 0)
            );
          });
        } else {
          // Reset percentages to empty string when unchecking
          this.specificPercentages.forEach(beneficiary => {
            this.$set(beneficiary, 'percentage', '');
          });
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .specific-percentages-container {
    padding-top: 28px;
    padding-bottom: 48px;

    .percentage-buttons-container {
      display: flex;
      gap: 8px;
      margin-bottom: 40px;
    }
    .add-item-btn {
      margin: 0;
    }

    .total-percentage-container {
      margin-top: 20px;
      margin-bottom: 20px;
      display: flex;
      flex-direction: row;
      align-items: center;

      .total-percentage {
        background-color: #fbf398;
        border: 2px solid #fced56;
        border-radius: 10px;
        font-size: 18px;
        font-weight: bold;
        padding: 10px;
        text-align: center;
        width: 100%;
      }
    }
  }

  .next-button {
    margin-top: 40px;
  }

  .list-item-btn {
    cursor: default;
  }

  .delete-btn {
    margin-top: 12px;
  }

  .small-delete-btn {
    min-width: 0;
    margin-top: 15px;
    margin-left: 12px;
  }
  .total-percentage-title {
    line-height: 28px;
  }
  @media screen and (min-width: 0px) and (max-width: 1263px) {
    .specific-percentages-container {
      .percentage-buttons-container {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
    }
  }
</style>
